import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { selectView, saveView, deleteView, autoSelectDefaultView, clearViewDirty, revertViewToPristine } from '../actions/ViewActions';
import ViewModal from './ViewModal';
import AlertModal from '../common/AlertModal';
import Sheet from '../common/Sheet';
import { arrangementsEqual } from '../util/arrangePages';
import { canRenameView, canArrangeView, canDeleteView, hasRowActions, canOverwriteView, categorizeViews, isRealView, searchViews, isCrossServerSlots } from '../util/viewListModel';
import './liveStyle.css';

/**
 * ViewList — the Views-sheet view manager (#86bb5dx62), replacing the native <select>
 * so each row can carry inline actions (David):
 *   • tap the row name  → select + watch that view (unsaved-changes guarded)
 *   • ✎ pencil          → load + Arrange that view in ONE click (no select-then-arrange 2-step)
 *   • ⋯ menu            → Rename & settings · Delete (manage ANY view; gated — see viewListModel)
 * A contextual "Save changes" banner appears only when the CURRENT view has genuine unsaved
 * edits vs its pristine layout (e.g. a live drag-swap). Reuses selectView/saveView/deleteView
 * + ViewModal, so the persistence path is identical to the old dropdown. Works dview + eview.
 *
 * Props:
 *   onArrange(viewName): select the view then open Arrange (owned by the host — Live or Playback)
 *   onArrangeCurrent(): arrange the CURRENTLY-loaded view (the "Arrange current view" entry). Optional;
 *                       when omitted (mobile has no host callback) the entry is not rendered. CSS also
 *                       hides .live-arrange-entries on mobile, so it's tablet+ regardless.
 *   onNewView(): start a blank New view (the "New view" entry). Optional, same gating as onArrangeCurrent.
 *   onClose(): close the Views sheet after a watch-select
 *   skipDirtyGuard: bypass the unsaved-changes switch prompt (playback #86bb7phhh — the user can
 *                   rearrange + save if they want, and the dirty dot + Save banner still show, but
 *                   switching views never opens the "save changes?" modal, David 2026-08-05)
 */
export const getViewDeviceType = (viewName, viewSlots, allCameras) => {
  const slots = viewSlots && viewSlots[viewName];
  if (!slots || !slots.length || !allCameras || !allCameras.length) return null;
  const camLookup = {};
  for (const c of allCameras) {
    if (c.isModern !== undefined) camLookup[`${c.bSerial}-${c.bID}`] = c.isModern;
  }
  if (Object.keys(camLookup).length === 0) return null;
  let hasModern = false, hasLegacy = false;
  for (const s of slots) {
    if (!s.bSerial || !s.bCamera) continue;
    const key = `${s.bSerial}-${s.bCamera}`;
    if (camLookup[key] === true) hasModern = true;
    else if (camLookup[key] === false) hasLegacy = true;
  }
  if (hasModern && hasLegacy) return 'mixed';
  if (hasModern) return 'modern';
  if (hasLegacy) return 'legacy';
  return null;
};

const ViewList = (props) => {
  const { views, currentView, cameras, pristineCameras, isDirty, isJumping, sSess, bUserID, viewRole, isAdmin, viewSlots,
          fEview, onArrange, onArrangeCurrent, onNewView, onClose, skipDirtyGuard, isPlayback, allCameras } = props;

  // Permission context for the view gates (#86bb7phhh, view-permission-model.md): admin = the
  // ADMIN right (state.user.fAdmin, NOT bRole); currentUserId = the logged-in user id (getSets
  // rows carry bUser). One ctx object threaded through every gate + the Save modal.
  const ctx = { isAdmin: !!isAdmin, currentUserId: bUserID };

  const [modal, setModal] = useState({ open: false, mode: null, view: null });
  const [rowMenu, setRowMenu] = useState(null);           // the view whose ⋯ menu is open
  const [unsaved, setUnsaved] = useState({ open: false, pending: null });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const alertTimer = useRef(null);
  useEffect(() => () => { if (alertTimer.current) clearTimeout(alertTimer.current); }, []);
  const showLegacyAlert = () => {
    if (alertTimer.current) clearTimeout(alertTimer.current);
    setAlert({ show: true, message: 'Playback is not available for legacy devices. Contact Dividia to discuss a cloud device upgrade.', type: 'info' });
    alertTimer.current = setTimeout(() => setAlert({ show: false, message: '', type: 'info' }), 4000);
  };

  // Type-ahead filter (David #86bb5dx62) — essential in eView where the list can be 300+
  // views. 200ms debounce, matching CameraTreePanel / CameraSelectDialog.
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const searchTimer = useRef(null);
  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);
  const onSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebounced(v), 200);
  };
  const clearSearch = () => { if (searchTimer.current) clearTimeout(searchTimer.current); setSearch(''); setDebounced(''); };

  const showAlert = (message, type = 'info') => setAlert({ show: true, message, type });

  // Genuine unsaved changes on the current view (vs its pristine snapshot) — drives the
  // contextual Save banner; falls back to the coarse flag if no snapshot exists yet.
  const currentDirty = (() => {
    if (!currentView || String(currentView).startsWith('__synthetic_')) return false;
    if (isJumping) return false;              // mid server-switch: state settles across systems, never flash "unsaved"
    if (!isDirty) return false;               // no real user edit → never dirty (a jump/view-load resets isDirty, so it can't false-light)
    if (!pristineCameras || !pristineCameras.length) return true;
    return !arrangementsEqual(cameras || [], pristineCameras);  // edited: dirty unless dragged back to the baseline
  })();

  const displayViews = (views || []).filter(isRealView);

  // ── selection (watch), guarded on unsaved changes ─────────────────────────
  const doSelect = (name) => { props.selectView(name); if (onClose) onClose(); };
  const onRowSelect = (name) => {
    if (name === currentView) { if (onClose) onClose(); return; }
    // Playback passes skipDirtyGuard: switching away just discards the rearrange (no "save
    // changes?" modal). The dirty dot + Save banner still show, so the user CAN save first if
    // they want — we just don't force the decision (David 2026-08-05).
    if (currentDirty && !skipDirtyGuard) { setUnsaved({ open: true, pending: name }); return; }
    doSelect(name);
  };
  const unsavedSave = () => {
    const existing = displayViews.find(v => v.sName === currentView);
    if (!existing) { setUnsaved({ open: false, pending: null }); return; }
    // A default (or auto-generated) view can't be saved over — David: rearranging a default can only be
    // kept as a NEW view. Route "Save & switch" into the Save-As-New modal instead of force-overwriting
    // (the modal's canOverwrite is false for a default, so it saves as new). Same gate as the modal.
    if (!canOverwriteView(existing, ctx)) {
      setUnsaved({ open: false, pending: null });
      setModal({ open: true, mode: 'save', view: existing });
      return;
    }
    props.saveView(sSess, bUserID, currentView, existing.fGlobal, existing.fDefault, true, null, existing.fDividia)
      .then(() => { props.clearViewDirty(); const p = unsaved.pending; setUnsaved({ open: false, pending: null }); if (p) doSelect(p); })
      .catch(e => showAlert('Error saving changes: ' + e.message, 'error'));
  };
  const unsavedDiscard = () => { props.clearViewDirty(); const p = unsaved.pending; setUnsaved({ open: false, pending: null }); if (p) doSelect(p); };

  // ⋯ row actions + permission gates live in util/viewListModel (tested): smart + auto-
  // generated views can't be renamed/deleted; a default can't be deleted; the ⋯ hides when
  // nothing's allowed. (canRenameView / canDeleteView / hasRowActions imported above.)
  const openRowMenu = (view) => setRowMenu(view);   // ⋯ shown on every row (symmetry, David)
  const closeRowMenu = () => setRowMenu(null);
  const openModal = (mode, view) => { closeRowMenu(); setModal({ open: true, mode, view }); };
  const closeModal = () => setModal({ open: false, mode: null, view: null });

  // ── contextual Save of the current view ───────────────────────────────────
  const openSaveCurrent = () => setModal({ open: true, mode: 'save', view: displayViews.find(v => v.sName === currentView) || null });

  const handleModalConfirm = (data) => {
    const { mode, view } = modal;
    const { sName, fGlobal, fDefault, fDividia, overwrite } = data;
    if (mode === 'delete') {
      props.deleteView(sSess, bUserID, view.sName, view.fGlobal)
        .then(() => { closeModal(); setTimeout(() => props.autoSelectDefaultView(), 100); })
        .catch(e => showAlert('Error deleting view: ' + e.message, 'error'));
      return;
    }
    const isUpdate = mode === 'edit' || (mode === 'save' && overwrite);
    const oldName = mode === 'edit' ? view.sName : null;
    props.saveView(sSess, bUserID, sName, fGlobal, fDefault, isUpdate, oldName, fDividia)
      .then((saved) => { props.clearViewDirty(); closeModal(); props.selectView(saved); })
      .catch(e => showAlert(`Error ${mode === 'edit' ? 'updating' : 'saving'} view: ${e.message}`, 'error'));
  };

  // Type-ahead filter, then categorize. ONE categorization for EVERY mode (#86bb7phhh,
  // view-permission-model.md): Default / Global / Smart / User, each view in EXACTLY one section,
  // User views filtered to the current user (privacy). The old eView 2-bucket workaround is retired
  // now that the controller's fSmart flag is trustworthy (BCC 6.2 verified) — the categories key off
  // fDefault / bUser / fSmart directly.
  const filtered = searchViews(views, debounced);
  const sections = categorizeViews(filtered, ctx);
  const showSearch = displayViews.length > 6;

  return (
    <div className="live-viewlist">
      <AlertModal show={alert.show} message={alert.message} type={alert.type} eviewMode={fEview} onClose={() => setAlert({ show: false, message: '', type: 'info' })} />

      {/* View management — Arrange the current view + start a blank New view. Lives INSIDE ViewList so
          Live AND Playback share ONE views list (David #86bb7phhh); .live-arrange-entries is CSS-gated to
          tablet+ (hidden on mobile). Rendered only when the host wires the callbacks. */}
      {(onArrangeCurrent || onNewView) && (
        <div className="live-arrange-entries">
          {onArrangeCurrent && (
            <button type="button" className="live-arrange-entry" onClick={onArrangeCurrent}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
              <span>Arrange current view</span>
            </button>
          )}
          {onNewView && (
            <button type="button" className="live-arrange-entry live-arrange-entry--new" onClick={onNewView}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
              <span>New view</span>
            </button>
          )}
        </div>
      )}

      {/* Type-ahead search (eView can list 300+ views). */}
      {showSearch && (
        <div className="live-viewlist__search">
          <span className="live-viewlist__search-ic" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z" /></svg>
          </span>
          <input type="text" className="live-viewlist__search-input" placeholder="Search views…" value={search} onChange={onSearch} />
          {search && <button type="button" className="live-viewlist__search-clear" onClick={clearSearch} aria-label="Clear search">×</button>}
        </div>
      )}

      {/* Contextual Save — only when the current view has unsaved edits (David). */}
      {currentDirty && currentView && (
        <div className="live-viewlist__save-banner">
          <span className="live-viewlist__save-text">Unsaved changes to “{currentView}”</span>
          <span className="live-viewlist__save-actions">
            <button className="live-viewlist__undo-btn" onClick={() => props.revertViewToPristine()} title="Undo changes — restore the saved view">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" /></svg>
              Undo
            </button>
            <button className="live-viewlist__save-btn" onClick={openSaveCurrent}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" /></svg>
              Save
            </button>
          </span>
        </div>
      )}

      {displayViews.length === 0 ? (
        <div className="live-viewlist__empty">No saved views</div>
      ) : sections.length === 0 ? (
        <div className="live-viewlist__empty">No views match “{search}”</div>
      ) : (
        sections.map(section => (
          <div key={section.key} className="live-viewlist__section">
            <div className="live-viewlist__section-label">{section.label}</div>
            {section.items.map(view => {
              const devType = getViewDeviceType(view.sName, viewSlots, allCameras);
              const playbackDisabled = isPlayback && devType === 'legacy';
              return (
              <div key={view.sName} className={'live-viewlist__row' + (view.sName === currentView ? ' live-viewlist__row--current' : '') + (playbackDisabled ? ' live-viewlist__row--disabled' : '')}>
                <button className="live-viewlist__name" onClick={() => playbackDisabled ? showLegacyAlert() : onRowSelect(view.sName)} title={playbackDisabled ? 'Playback not available for legacy devices' : view.sName}>
                  {view.sName === currentView && <span className="live-viewlist__current-dot" aria-label="Current view" />}
                  <span className="live-viewlist__name-text">{view.sName}</span>
                  {devType && <span className={`live-viewlist__device-pill live-viewlist__device-pill--${devType}`}>{devType === 'mixed' ? 'Mixed' : devType === 'modern' ? 'Modern' : 'Legacy'}</span>}
                  {playbackDisabled && <span className="live-viewlist__info-ic" aria-label="Info"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg></span>}
                </button>
                {/* Row controls (#86bb7phhh, view-permission-model.md): ✎ Arrange (any real view —
                    rearranging a Default/Global/Smart persists as a NEW view via Save-As) + a ⋯ menu
                    (rename/settings/delete) shown only when the current user may modify THIS view.
                    Same for dView + eView now that the controller's view-type flags are trustworthy. */}
                {onArrange && canArrangeView(view) && (
                <button className="live-viewlist__act" title="Arrange this view" aria-label={`Arrange ${view.sName}`} onClick={() => onArrange && onArrange(view.sName)}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                </button>
                )}
                {hasRowActions(view, ctx) && (
                <button className="live-viewlist__act" title="More" aria-label={`More actions for ${view.sName}`} onClick={() => openRowMenu(view)}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>
                </button>
                )}
              </div>
              );
            })}
          </div>
        ))
      )}

      {/* ⋯ row-actions sheet */}
      {rowMenu && (
        <Sheet
          className="live-sheet live-tile-sheet"
          scrimClassName="live-sheet-scrim"
          title={rowMenu.sName}
          ariaLabel={`Actions for ${rowMenu.sName}`}
          onClose={closeRowMenu}
        >
            {/* Views with no available actions explain why (Smart = server/aggregation-derived;
                auto-generated = computed chrome; Default/Global = admin-only for this user). */}
            {!hasRowActions(rowMenu, ctx) && (
              <p className="live-viewlist__menu-note">
                {rowMenu.isSmart
                  ? 'Smart views are generated automatically from matching cameras — they can’t be renamed or deleted.'
                  : rowMenu.isGenerated
                    ? 'This is an auto-generated default view — it can’t be renamed or deleted.'
                    : 'Only an administrator can rename or delete this view.'}
              </p>
            )}
            {/* Rename & settings (incl. the Default + Global toggles) — real stored views only. */}
            {canRenameView(rowMenu, ctx) && (
              <button className="live-tile-action" onClick={() => openModal('edit', rowMenu)}>
                <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg></span>
                <span>Rename &amp; settings</span>
              </button>
            )}
            {canDeleteView(rowMenu, ctx) && (
              <button className="live-tile-action live-tile-action--danger" onClick={() => openModal('delete', rowMenu)}>
                <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg></span>
                <span>Delete</span>
              </button>
            )}
            <button className="live-tile-action" onClick={closeRowMenu}>
              <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg></span>
              <span>Cancel</span>
            </button>
        </Sheet>
      )}

      {/* Unsaved-changes prompt when switching views */}
      {unsaved.open && (
        <Sheet
          className="live-sheet live-tile-sheet"
          scrimClassName="live-sheet-scrim"
          title="Unsaved changes"
          ariaLabel="Unsaved changes"
          onClose={() => setUnsaved({ open: false, pending: null })}
        >
            <p style={{ margin: '-4px 0 10px', fontSize: 'var(--live-font-xs)', color: 'var(--theme-text-muted)' }}>
              Save changes to “{currentView}” before switching?
            </p>
            <button className="live-tile-action" onClick={unsavedSave}>
              <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z" /></svg></span>
              <span>Save &amp; switch</span>
            </button>
            <button className="live-tile-action live-tile-action--danger" onClick={unsavedDiscard}>
              <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg></span>
              <span>Discard &amp; switch</span>
            </button>
            <button className="live-tile-action" onClick={() => setUnsaved({ open: false, pending: null })}>
              <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg></span>
              <span>Keep editing</span>
            </button>
        </Sheet>
      )}

      {/* Rename / Save / Delete modal (reuses the unified ViewModal) */}
      <ViewModal
        mode={modal.mode}
        open={modal.open}
        onClose={closeModal}
        onConfirm={handleModalConfirm}
        currentView={modal.view}
        allViews={views || []}
        viewRole={viewRole}
        isAdmin={!!isAdmin}
        currentUserId={bUserID}
        crossServer={isCrossServerSlots(
          modal.mode === 'edit' && modal.view
            ? ((viewSlots && viewSlots[modal.view.sName]) || [])
            : (cameras || [])
        )}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  views: state.view.views,
  currentView: state.view.currentView,
  viewSlots: state.view.viewSlots,
  pristineCameras: state.view.pristineCameras,
  isDirty: state.view.isDirty,
  isJumping: state.navigation.isJumping,
  cameras: state.server.cameras,
  allCameras: state.server.allCameras,
  fEview: state.server.fEview,
  activeServerSerial: state.server.activeServerSerial,
  useEviewLogin: state.auth.useEviewLogin,
  sSess: state.auth.sSess,
  bUserID: state.user.bID,
  viewRole: state.user.bRole,
  isAdmin: !!state.user.fAdmin,
});

export { ViewList };
export default connect(mapStateToProps, { selectView, saveView, deleteView, autoSelectDefaultView, clearViewDirty, revertViewToPristine })(ViewList);
